import React, { useContext, useEffect, useState, useRef } from "react";
import { Container, Tab, Nav, Card, Button, Table, Form, InputGroup, Spinner, Alert, Badge, Row, Col, Modal } from "react-bootstrap";
import Select from "react-select";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import { AuthContext } from "../context/AuthContext";
import axios from "../context/AxiosInstance";
import { toast } from "react-toastify";
import EmployeMultiPicker from "../components/impression/EmployeMultiPicker";
import RhTablePagination from "../components/common/RhTablePagination";
import { FaPrint } from "react-icons/fa";
import "../styles/rh-modern-ui.css";

const buildEmployeOptionsFromContrats = (contrats) => {
  const seen = new Set();
  const opts = [];
  for (const c of contrats || []) {
    const idEmp = c.id_employe;
    if (idEmp && !seen.has(idEmp)) {
      seen.add(idEmp);
      const nom = c.nom_employe || "";
      const prenom = c.prenom_employe || "";
      const label = `${nom} ${prenom}`.trim() || `Employé #${idEmp}`;
      opts.push({
        value: String(idEmp),
        label,
        nom,
        prenom,
        poste: c.poste || "",
        departement: c.departement || "",
      });
    }
  }
  return opts.sort((a, b) => a.label.localeCompare(b.label, "fr"));
};

const moisOptions = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];
const annees = [2024, 2025, 2026];

const ImpressionDocumentsPage = () => {
  const { role } = useContext(AuthContext);
  const [active, setActive] = useState("attestation");

  // Commun
  const [mois, setMois] = useState("");
  const [annee, setAnnee] = useState("");

  // Data states
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [printLoading, setPrintLoading] = useState(false);
  const [printAttestLoading, setPrintAttestLoading] = useState(false);

  useEffect(() => {
    if (!role || (role !== "RH" && role !== "ADMIN")) return;
  }, [role]);

  const canLoadMonthly = () => mois && annee;

  const fetchBulletinsGeneres = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/bulletins/admin/list", { params: { page: 1, per_page: 200, search } });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      // Optionnel: filtrer par mois/année si fournis
      const filtered = list.filter(b => (!mois || b.mois === mois) && (!annee || String(b.annee) === String(annee)));
      setItems(filtered);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  };

  const fetchEmployes = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/employes/", { params: { is_active: 1 } });
      setItems(res.data?.data || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  };

  const [selectedEmployeId, setSelectedEmployeId] = useState("");
  const [selectedEmployeAtt, setSelectedEmployeAtt] = useState("");
  const [employesOptions, setEmployesOptions] = useState([]);
  const [selectedMulti, setSelectedMulti] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchStatuses, setBatchStatuses] = useState({}); // id -> 'pending'|'ok'|'error'
  const [anneeMulti, setAnneeMulti] = useState("");
  // Rapport paiements (mois)
  const [rpAnnee, setRpAnnee] = useState("");
  const [rpMois, setRpMois] = useState("");
  const [rpLoading, setRpLoading] = useState(false);
  const [rpData, setRpData] = useState(null); // { kpis, sans_verrouille, verrouilles }
  const [rpPrint, setRpPrint] = useState(false);
  const [rpJournal, setRpJournal] = useState([]); // [{jour,count}]
  // Datatable states (indépendants des KPI)
  const [svSearch, setSvSearch] = useState("");
  const [svPage, setSvPage] = useState(1);
  const [avSearch, setAvSearch] = useState("");
  const [avPage, setAvPage] = useState(1);

  const formatAmount = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString();
  };
  const formatCurrency = (v) => `${formatAmount(v)} GNF`;

  // Generic lightweight data table (search, sort, paginate) without external deps
  const DataTable = ({ columns, rows, selectable = false, rowIdKey = 'id', selectedIds = [], onToggleRow, onToggleAll, defaultPageSize = 10 }) => {
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState(columns[0]?.key || "");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const filtered = (rows || []).filter((r) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return Object.values(r).some((val) => String(val ?? "").toLowerCase().includes(s));
    });

    const sorted = [...filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return sortDir === "asc" ? -1 : 1;
      if (vb == null) return sortDir === "asc" ? 1 : -1;
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const start = (page - 1) * pageSize;
    const visible = sorted.slice(start, start + pageSize);

    const toggleSort = (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
      setPage(1);
    };

    return (
      <div className="rh-table-panel">
        <div className="rh-toolbar border-0 shadow-none mb-0" style={{ borderRadius: 0, borderBottom: "1px solid #e2e8f0" }}>
          <div className="rh-search-wrap" style={{ maxWidth: 320 }}>
            <Form.Control placeholder="Rechercher dans le tableau…" value={search} onChange={(e)=>{ setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>
        <div className="table-responsive">
          <Table className="mb-0 rh-modern-table">
            <thead className="table-light">
              <tr>
                {selectable && (
                  <th style={{ width: 36 }}>
                    <Form.Check
                      type="checkbox"
                      onChange={(e)=>{
                        if (typeof onToggleAll === 'function') {
                          onToggleAll(visible, e.target.checked);
                        }
                      }}
                    />
                  </th>
                )}
                {columns.map((c) => (
                  <th key={c.key} onClick={()=>toggleSort(c.key)} style={{ cursor: 'pointer' }}>
                    {c.header} {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, idx) => (
                <tr key={idx}>
                  {selectable && (
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(String(r[rowIdKey]))}
                        onChange={(e)=>{
                          if (typeof onToggleRow === 'function') onToggleRow(r, e.target.checked);
                        }}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key}>{c.render ? c.render(r[c.key], r) : r[c.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <RhTablePagination
          currentPage={page}
          totalItems={sorted.length}
          perPage={pageSize}
          onPageChange={setPage}
          onPerPageChange={(n) => { setPageSize(n); setPage(1); }}
          itemLabel="lignes"
        />
      </div>
    );
  };

  // Liste employes - génération personnalisée
  const varOptions = [
    { value: 'nom', label: 'Nom' },
    { value: 'prenom', label: 'Prénom' },
    { value: 'email', label: 'Email' },
    { value: 'poste', label: 'Poste' },
    { value: 'departement', label: 'Département' },
    { value: 'service', label: 'Service' },
    { value: 'lieu', label: 'Lieu' },
    { value: 'matricule', label: 'Matricule' },
    { value: 'genre', label: 'Genre' },
    { value: 'type_cadre', label: 'Type de cadre' },
    { value: 'date_embauche', label: 'Date d\'embauche' },
    { value: 'conges_restants', label: 'Congés restants' },
  ];
  const [empSelectedVars, setEmpSelectedVars] = useState([]); // values only
  const [empSelectedIds, setEmpSelectedIds] = useState([]); // values only
  const [contracts, setContracts] = useState([]);
  const [congesByEmp, setCongesByEmp] = useState({});
  const [empFilterDept, setEmpFilterDept] = useState("");
  const [empFilterPoste, setEmpFilterPoste] = useState("");
  const [empFilterLieu, setEmpFilterLieu] = useState("");
  const empHeaderRef = useRef(null);
  const [empHeaderError, setEmpHeaderError] = useState("");
  const [empLoading, setEmpLoading] = useState(false);
  const [empSignature, setEmpSignature] = useState(false);
  

  // Charger contrats et conges pour enrichir la prévisualisation
  useEffect(() => {
    const load = async () => {
      try {
        const resCtr = await axios.get('/contrats/');
        setContracts(Array.isArray(resCtr.data?.data) ? resCtr.data.data : []);
      } catch {}
      try {
        const resCg = await axios.get('/conges/', { params: { is_active: 1 } });
        const map = {};
        (resCg.data?.data || []).forEach(c => {
          const id = c.id_employe;
          const acquis = Number(c.jours_acquis_conge) || 0;
          const pris = Number(c.jours_pris_conge) || 0;
          const restant = acquis - pris;
          map[id] = (map[id] || 0) + restant;
        });
        setCongesByEmp(map);
      } catch {}
    };
    if (active === 'employes') load();
  }, [active]);

  const mergedEmployees = (Array.isArray(items) ? items : []).map(e => {
    const c = (contracts || []).find(x => x.id_employe === e.id_employe && x.is_active);
    // recalcul conges restants dynamique: acquis - pris (si disponible sur items congesByEmp stocké seulement restants -> on garde pour fallback)
    const cr = congesByEmp[e.id_employe] ?? 0;
    return {
      ...e,
      poste: c?.poste || '',
      departement: c?.departement || '',
      service: c?.service || '',
      lieu: c?.lieu || '',
      matricule: e.matricule_employe,
      conges_restants: cr,
    };
  });

  const handleGenerateEmployeesDoc = async () => {
    if (empSelectedVars.length < 5 || empSelectedIds.length === 0) return;
    const headerVal = (empHeaderRef.current && typeof empHeaderRef.current.value === 'string') ? empHeaderRef.current.value : '';
    if (!headerVal || !headerVal.trim()) {
      setEmpHeaderError("Veuillez saisir le titre du document");
      try { const { toast } = await import('react-toastify'); toast.error('Saisissez le titre du document avant de générer.'); } catch {}
      return;
    }
    setEmpHeaderError("");
    setEmpLoading(true);
    try {
      // Trier les IDs sélectionnés par ordre croissant
      const sortedIds = empSelectedIds.map(v => Number(v)).sort((a, b) => a - b);
      
      const payload = { 
        variables: empSelectedVars, 
        ids: sortedIds, 
        header: headerVal, 
        pdf: 0
      };
      console.log('🔍 Frontend - Payload envoyé:', payload);
      console.log('🔍 Frontend - empSignature:', empSignature, 'type:', typeof empSignature);
      const resp = await axios.post(`/rapports/employes/imprimer?signature=${empSignature}`, payload, { responseType: 'blob' });
      const contentType = resp.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const fileName = `liste_employes.docx`;
      const blob = new Blob([resp.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
    finally { setEmpLoading(false); }
  };

  const fetchEmployesOptions = async () => {
    try {
      const resCtr = await axios.get("/contrats/");
      const contrats = Array.isArray(resCtr.data?.data) ? resCtr.data.data : [];
      const opts = buildEmployeOptionsFromContrats(contrats);
      setEmployesOptions(opts);
      if (!selectedEmployeId && opts[0]) setSelectedEmployeId(String(opts[0].value));
    } catch (e) {
      setEmployesOptions([]);
    }
  };

  const fetchConges = async () => {
    setLoading(true);
    try {
      // Charger la liste des employés AYANT un contrat (unique) une fois
      if (employesOptions.length === 0) {
        const resCtr = await axios.get("/contrats/");
        const contrats = Array.isArray(resCtr.data?.data) ? resCtr.data.data : [];
        const opts = buildEmployeOptionsFromContrats(contrats);
        setEmployesOptions(opts);
        if (!selectedEmployeId && opts[0]) setSelectedEmployeId(String(opts[0].value));
      }

      if (!selectedEmployeId) { setItems({}); setLoading(false); return; }
      const res = await axios.get("/conges/situation/context", { params: { id_employe: selectedEmployeId, annee: annee || undefined } });
      const ctx = res.data?.context;
      // Normaliser structure pour éviter .map sur non-array
      const safe = {
        ...(ctx && typeof ctx === 'object' ? ctx : {}),
        conges: Array.isArray(ctx?.conges) ? ctx.conges : [],
      };
      setItems(safe);
    } catch (e) { setItems({}); } finally { setLoading(false); }
  };

  // Charger la liste des employés automatiquement quand on arrive sur l'onglet congés/attestation/employés
  useEffect(() => {
    if ((active === "conges" || active === "attestation" || active === "employes") && employesOptions.length === 0) {
      fetchEmployesOptions();
    }
    if (active === "employes") {
      // Charger la liste brute des employés pour la prévisualisation
      fetchEmployes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Déclencher automatiquement le chargement du contexte dès que employé + année sont sélectionnés
  useEffect(() => {
    if (active === "conges" && selectedEmployeId && annee) {
      fetchConges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedEmployeId, annee]);

  const fetchPaiementsMois = async () => {
    // En attendant un endpoint dédié, on agrège via salaires et crg sur la page
    if (!canLoadMonthly()) return;
    setLoading(true);
    try {
      const res = await axios.get("/salaires/", { params: { mois, annee } });
      setItems(res.data?.data || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  };

  const onLoad = async () => {
    if (active === "bulletins") return fetchBulletinsGeneres();
    if (active === "employes") return fetchEmployes();
    if (active === "conges") return fetchConges();
    if (active === "paiements") return fetchPaiementsMois();
  };

  const fetchRapportPaiement = async () => {
    if (!rpAnnee || !rpMois) return;
    setRpLoading(true);
    try {
      const res = await axios.get('/rapports/paiement-mensuel', { params: { annee: rpAnnee, mois: rpMois } });
      setRpData(res.data || null);
      // Charger journal
      try {
        const jr = await axios.get('/rapports/paiement-mensuel/journal', { params: { annee: rpAnnee, mois: rpMois } });
        setRpJournal(jr.data?.data || []);
      } catch {}
    } catch {
      setRpData(null);
    } finally {
      setRpLoading(false);
    }
  };

  const exportRapportSection = async (section) => {
    if (!rpAnnee || !rpMois) return;
    try {
      const resp = await axios.get('/rapports/paiement-mensuel/export', { params: { annee: rpAnnee, mois: rpMois, section }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `rapport_${section}_${rpMois}_${rpAnnee}.csv`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch {}
  };

  const printRapport = async () => {
    if (!rpAnnee || !rpMois) return;
    setRpPrint(true);
    try {
      const resp = await axios.post('/rapports/paiement-mensuel/imprimer', null, { params: { annee: rpAnnee, mois: rpMois }, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `rapport_paiement_${rpMois}_${rpAnnee}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch {}
    finally { setRpPrint(false); }
  };

  // Auto-chargement du rapport dès que Année + Mois sont sélectionnés
  useEffect(() => {
    if (active === "paiements" && rpAnnee && rpMois) {
      fetchRapportPaiement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, rpAnnee, rpMois]);

  const RHListFiches = () => {
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [searchF, setSearchF] = useState("");
    const [anneeF, setAnneeF] = useState("");
    const [empF, setEmpF] = useState("");
    const [loadingF, setLoadingF] = useState(false);

    const load = async () => {
      setLoadingF(true);
      try {
        const res = await axios.get('/conges/admin/list', { params: { page, per_page: perPage, search: searchF || undefined, annee: anneeF || undefined, id_employe: empF || undefined } });
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        setList(arr.filter(it => it && typeof it === 'object'));
        setTotal(Number(res.data?.total || arr.length || 0));
      } finally { setLoadingF(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, perPage]);
    useEffect(() => { const t = setTimeout(()=>{ setPage(1); load(); }, 400); return ()=>clearTimeout(t); /* eslint-disable-next-line */ }, [searchF, anneeF, empF]);

    return (
      <>
        <div className="d-flex align-items-center gap-2 mb-2">
          <Form.Control placeholder="Rechercher (nom, matricule, année)" value={searchF} onChange={(e)=>setSearchF(e.target.value)} style={{ maxWidth: 360 }} />
          <Form.Select value={anneeF} onChange={(e)=>setAnneeF(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="">Année</option>
            {annees.map(y => (<option key={y} value={y}>{y}</option>))}
          </Form.Select>
          <div style={{ minWidth: 320 }}>
            <Select
              classNamePrefix="react-select"
              options={employesOptions}
              isSearchable
              placeholder="Filtrer par employé"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
              maxMenuHeight={380}
              styles={{ menuPortal: (b)=>({ ...b, zIndex: 9999 }) }}
              value={empF ? employesOptions.find(o => String(o.value) === String(empF)) : null}
              onChange={(opt)=> setEmpF(opt ? String(opt.value) : "")}
              isClearable
            />
          </div>
          <small className="text-muted">Total: {total}</small>
        </div>
        {loadingF ? (
          <div className="text-center py-3"><Spinner animation="border" /></div>
        ) : (
          <div className="table-responsive">
            <Table className="mb-0 table-soft">
              <thead className="table-light"><tr><th>Employé</th><th>Matricule</th><th>Année</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {list.map((f) => (
                  <tr key={f.id_fiche}>
                    <td>{f.employe?.nom} {f.employe?.prenom}</td>
                    <td>{f.employe?.matricule}</td>
                    <td><Badge bg="secondary">{f.annee || '—'}</Badge></td>
                    <td>{f.date_generation}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={async()=>{
                        try {
                          const resp = await axios.get(`/conges/admin/telecharger/${f.id_fiche}`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([resp.data]));
                          const link = document.createElement('a');
                          link.href = url; link.setAttribute('download', f.nom_fichier || 'fiche_conge.pdf');
                          document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
                        } catch(e) {}
                      }}>📥 Télécharger</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <RhTablePagination
              currentPage={page}
              totalItems={total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
              itemLabel="fiches"
              serverMode
            />
          </div>
        )}
      </>
    );
  };

  // Gestion manuelle des blocs pliables pour éviter les fermetures inattendues
  const [openAcc, setOpenAcc] = useState(''); // 'unique' | 'multiple' | 'liste' — vide = tous fermés

  const handlePrint = async () => {
    if (active === "conges") {
      if (!selectedEmployeId) return;
      try {
        setPrintLoading(true);
        const resp = await axios.post("/conges/imprimer-fiche", null, {
          params: { id_employe: selectedEmployeId, annee: annee || undefined },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `conge_${selectedEmployeId}${annee ? '_' + annee : ''}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        let msg = "Erreur lors de l'impression de la fiche";
        try {
          if (e?.response?.data) {
            const reader = new FileReader();
            await new Promise((resolve) => {
              reader.onload = () => {
                try {
                  const text = reader.result;
                  try {
                    const j = JSON.parse(text);
                    msg = j.detail || j.message || text;
                  } catch {
                    msg = text || msg;
                  }
                } catch {}
                resolve();
              };
              reader.readAsText(e.response.data);
            });
          } else if (e?.message) {
            msg = e.message;
          }
        } catch {}
        try { const { toast } = await import('react-toastify'); toast.error(msg); } catch {}
      } finally { setPrintLoading(false); }
      return;
    }
    window.print();
  };

  const Toolbar = (
    <Card className="mb-3 shadow-sm surface-card toolbar-card">
       
    </Card>
  );

  const SectionContent = () => {
    if (loading) return <div className="text-center py-4"><Spinner animation="border"/></div>;
    // N'afficher l'état vide que pour les onglets qui dépendent des données (pas 'conges', 'attestation', 'paiements', ni 'employes')
    if (!['conges','attestation','paiements','employes'].includes(active) && (!items || items.length === 0)) return <Alert variant="light" className="text-center">Aucun élément</Alert>;
    if (active === "employes") {
      return (
        <>
          <Card className="mb-3 shadow-sm surface-card toolbar-card">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={12} sm={12}>
                  <div className="small text-muted d-flex justify-content-between align-items-center">
                    <span>Variables (exactement 5)</span>
                    <span className="text-muted">{empSelectedVars.length}/5</span>
                  </div>
                  <Select
                    isMulti
                    classNamePrefix="react-select"
                    options={varOptions}
                    value={varOptions.filter(o => empSelectedVars.includes(o.value))}
                    onChange={(vals)=> setEmpSelectedVars((vals||[]).map(v=>v.value))}
                    isOptionDisabled={(opt)=> (empSelectedVars.length >= 5) && !empSelectedVars.includes(opt.value)}
                    placeholder="Choisir des variables..."
                  />
                  {empSelectedVars.length !== 5 && (
                    <small className="text-danger">Veuillez sélectionner exactement 5 variables.</small>
                  )}
                </Col>
                <Col md={12} sm={12}>
                  <div className="small text-muted mb-2">Filtrer & Sélectionner des employés</div>
                  <Row className="g-2 mb-2">
                    <Col md={4} sm={12}>
                      <Form.Select value={empFilterDept} onChange={(e)=>setEmpFilterDept(e.target.value)}>
                        <option value="">Département (tous)</option>
                        {Array.from(new Set(mergedEmployees.map(e=> e.departement).filter(Boolean))).map((d)=>(<option key={d} value={d}>{d}</option>))}
                      </Form.Select>
                    </Col>
                    <Col md={4} sm={12}>
                      <Form.Select value={empFilterPoste} onChange={(e)=>setEmpFilterPoste(e.target.value)}>
                        <option value="">Poste (tous)</option>
                        {Array.from(new Set(mergedEmployees.map(e=> e.poste).filter(Boolean))).map((p)=>(<option key={p} value={p}>{p}</option>))}
                      </Form.Select>
                    </Col>
                    <Col md={4} sm={12}>
                      <Form.Select value={empFilterLieu} onChange={(e)=>setEmpFilterLieu(e.target.value)}>
                        <option value="">Lieu (tous)</option>
                        {Array.from(new Set(mergedEmployees.map(e=> e.lieu).filter(Boolean))).map((l)=>(<option key={l} value={l}>{l}</option>))}
                      </Form.Select>
                    </Col>
                  </Row>
                  <DataTable
                    selectable
                    rowIdKey="id_employe"
                    selectedIds={empSelectedIds}
                    onToggleRow={(row, checked)=>{
                      const id = String(row.id_employe);
                      setEmpSelectedIds((prev)=> checked ? Array.from(new Set([...prev, id])) : prev.filter(x=>x!==id));
                    }}
                    onToggleAll={(visible, checked)=>{
                      const ids = visible.map(v=> String(v.id_employe));
                      setEmpSelectedIds((prev)=> {
                        if (checked) return Array.from(new Set([...prev, ...ids]));
                        const h = new Set(ids);
                        return prev.filter(x=> !h.has(x));
                      });
                    }}
                    columns={[{ key:'nom', header:'Nom' }, { key:'prenom', header:'Prénom' }, { key:'poste', header:'Poste' }, { key:'departement', header:'Département' }, { key:'lieu', header:'Lieu' }]}
                    rows={mergedEmployees.filter(e=> (!empFilterDept || e.departement===empFilterDept) && (!empFilterPoste || e.poste===empFilterPoste) && (!empFilterLieu || e.lieu===empFilterLieu))}
                  />
                  <div className="mt-2 d-flex gap-2">
                    <Button size="sm" variant="secondary" onClick={()=> setEmpSelectedIds(mergedEmployees.map(e=> String(e.id_employe)))}>Tout sélectionner</Button>
                    <Button size="sm" variant="outline-secondary" onClick={()=> setEmpSelectedIds([])}>Tout désélectionner</Button>
                  </div>
                </Col>
                <Col xs={12} className="d-flex align-items-end gap-3 flex-wrap">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="empSignature" 
                      checked={empSignature} 
                      onChange={(e)=>setEmpSignature(e.target.checked)} 
                    />
                    <label className="form-check-label" htmlFor="empSignature">
                      Signature
                    </label>
                  </div>
                  <Button variant="success" disabled={empSelectedVars.length!==5 || empSelectedIds.length===0 || empLoading} onClick={handleGenerateEmployeesDoc}>
                    {empLoading ? (<><Spinner size="sm" animation="border" className="me-2"/>Génération...</>) : 'Générer document'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="table-responsive">
            <Table className="mb-0 table-soft">
              <thead className="table-light">
                <tr>
                  {empSelectedVars.map((v)=>(<th key={v}>{v.replace('_',' ').toUpperCase()}</th>))}
                </tr>
              </thead>
              <tbody>
                {mergedEmployees.filter(e => empSelectedIds.includes(String(e.id_employe))).map((e, idx) => (
                  <tr key={idx}>
                    {empSelectedVars.map((v)=>(<td key={v}>{String(e[v] ?? '')}</td>))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Card className="mt-3">
            <Card.Body>
              <div className="small text-muted mb-1">Titre du document</div>
              <Form.Control
                ref={empHeaderRef}
                defaultValue={'Liste des Employés'}
                onInput={(e)=> { empHeaderRef.current = e.target; if (empHeaderError) setEmpHeaderError(''); }}
                onChange={(e)=> { /* on ne touche pas au caret */ if (empHeaderError) setEmpHeaderError(''); }}
                placeholder="Ex: Liste des Employés"
                isInvalid={!!empHeaderError}
              />
              {empHeaderError ? (
                <div className="invalid-feedback d-block">{empHeaderError}</div>
              ) : null}
              {!empHeaderError && (!(empHeaderRef.current?.value || '').trim()) ? (
                <small className="text-danger">Saisissez le titre du document avant de générer.</small>
              ) : null}
            </Card.Body>
          </Card>

          {/* Modal supprimée: saisie du titre intégrée à la barre d'actions */}
        </>
      );
    }
    if (active === "conges") {
      const ctx = items || {};
      const rows = Array.isArray(ctx.conges) ? ctx.conges : [];
      return (
        <>
          {/* Blocs pliables: Impression unique, Impression multiple, Liste des impressions */}
          <div className="accordion mb-3" id="accordionFicheConges">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button className="accordion-button" type="button" onClick={()=>setOpenAcc(openAcc==='unique' ? '' : 'unique')} aria-expanded={openAcc==='unique'}>
                  🧾 Impression unique
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${openAcc==='unique' ? 'show' : ''}`} aria-labelledby="headingOne">
                <div className="accordion-body">
                  <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                    <div>
                      <div className="fw-bold">Employé</div>
                      <div>{ctx.employe?.nom} {ctx.employe?.prenom} — {ctx.employe?.matricule}</div>
                      <div className="text-muted small">{ctx.employe?.poste} · {ctx.employe?.departement}</div>
                    </div>
                    <div>
                      <div className="fw-bold">Période</div>
                      <div>{ctx.periode?.annee || "Toutes années"}</div>
                    </div>
                  </div>
                  <Row className="g-2 mb-3 align-items-end">
                    <Col md={8} sm={12}>
                      <Select
                        classNamePrefix="react-select"
                        options={employesOptions}
                        isSearchable
                        placeholder="Rechercher un employé..."
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        maxMenuHeight={480}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        value={selectedEmployeId ? employesOptions.find(o => String(o.value) === String(selectedEmployeId)) : null}
                        onChange={(opt) => setSelectedEmployeId(opt ? String(opt.value) : "")}
                      />
                    </Col>
                    <Col md={2} sm={6}>
                      <Form.Select value={annee} onChange={(e) => { const v = e.target.value; setAnnee(v); }}>
                        <option value="">Année</option>
                        {annees.map((y) => (<option key={y} value={y}>{y}</option>))}
                      </Form.Select>
                    </Col>
                    <Col md={2} sm={6}>
                      <Button
                        className="w-100"
                        variant="success"
                        onClick={handlePrint}
                        disabled={printLoading || !selectedEmployeId || !annee}
                      >
                        {printLoading ? (<><Spinner size="sm" animation="border" className="me-2"/>Impression...</>) : "Imprimer fiche congés"}
                      </Button>
                    </Col>
                  </Row>
                  <div className="table-responsive">
                    <Table className="mb-0 table-soft">
                      <thead className="table-light"><tr><th>Mois</th><th>Intervalle</th><th>Jours acquis</th><th>Jours pris</th><th>Restants</th><th>Enregistré le</th></tr></thead>
                      <tbody>{Array.isArray(rows) ? rows.map((r,i)=>(<tr key={i}><td>{r.mois} {r.annee}</td><td>{r.intervalle}</td><td>{r.jours_acquis}</td><td>{r.jours_pris}</td><td>{r.jours_restants}</td><td>{r.date_enregistrement}</td></tr>)) : null}</tbody>
                    </Table>
                  </div>
                  <div className="row g-3 mt-3">
                    <div className="col-md-4"><Card className="text-center border-success h-100"><Card.Body><div className="text-success small">Congés acquis</div><div className="h5 fw-bold">{ctx.kpi?.somme_acquis || 0}</div></Card.Body></Card></div>
                    <div className="col-md-4"><Card className="text-center border-danger h-100"><Card.Body><div className="text-danger small">Congés pris</div><div className="h5 fw-bold">{ctx.kpi?.somme_pris || 0}</div></Card.Body></Card></div>
                    <div className="col-md-4"><Card className="text-center border-primary h-100"><Card.Body><div className="text-primary small">Restant</div><div className="h5 fw-bold">{ctx.kpi?.restant || 0}</div></Card.Body></Card></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="headingTwo">
                <button className="accordion-button ${openAcc==='multiple' ? '' : 'collapsed' }" type="button" onClick={()=>setOpenAcc(openAcc==='multiple' ? '' : 'multiple')} aria-expanded={openAcc==='multiple'}>
                  🧩 Impressions multiples et Envoie dans l'espace personnel
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${openAcc==='multiple' ? 'show' : ''}`} aria-labelledby="headingTwo">
                <div className="accordion-body">
                  <p className="text-muted small mb-3">
                    Cochez les employés dans la liste, choisissez l&apos;année, puis lancez la génération. Les fiches seront enregistrées dans l&apos;espace personnel de chaque employé.
                  </p>

                  <EmployeMultiPicker
                    options={employesOptions}
                    selected={selectedMulti}
                    onChange={setSelectedMulti}
                    disabled={batchRunning}
                    title="Employés à inclure"
                  />

                  <Row className="g-3 mt-3 align-items-end">
                    <Col md={3} sm={6}>
                      <Form.Label className="fw-semibold small text-uppercase text-muted">Année</Form.Label>
                      <Form.Select value={anneeMulti} onChange={(e) => setAnneeMulti(e.target.value)} disabled={batchRunning}>
                        <option value="">— Sélectionner —</option>
                        {annees.map((y) => (<option key={y} value={y}>{y}</option>))}
                      </Form.Select>
                    </Col>
                    <Col md="auto">
                      <Button
                        variant="primary"
                        className="px-4"
                        onClick={async () => {
                          if (!anneeMulti || selectedMulti.length === 0) return;
                          setBatchRunning(true);
                          const statuses = {};
                          setBatchStatuses({});
                          for (const opt of selectedMulti) {
                            statuses[opt.value] = "pending";
                            setBatchStatuses({ ...statuses });
                            try {
                              await axios.post("/conges/imprimer-fiche", null, {
                                params: { id_employe: opt.value, annee: anneeMulti, enregistrer: 1 },
                                responseType: "blob",
                              });
                              statuses[opt.value] = "ok";
                              setBatchStatuses({ ...statuses });
                            } catch (e) {
                              statuses[opt.value] = "error";
                              setBatchStatuses({ ...statuses });
                            }
                          }
                          setBatchRunning(false);
                          toast.success("Génération multiple terminée");
                        }}
                        disabled={batchRunning || !anneeMulti || selectedMulti.length === 0}
                      >
                        {batchRunning ? (
                          <><Spinner size="sm" animation="border" className="me-2" />Génération en cours…</>
                        ) : (
                          `Générer ${selectedMulti.length > 0 ? selectedMulti.length : ""} fiche(s)`
                        )}
                      </Button>
                    </Col>
                  </Row>

                  {(!anneeMulti || selectedMulti.length === 0) && (
                    <Alert variant="light" className="mt-3 mb-0 border small">
                      Sélectionnez au moins un employé et une année pour activer la génération.
                    </Alert>
                  )}

                  {selectedMulti.length > 0 && (
                    <div className="table-responsive mt-3 border rounded overflow-hidden">
                      <Table className="mb-0">
                        <thead style={{ background: "#1e293b", color: "#f8fafc" }}>
                          <tr>
                            <th>Employé</th>
                            <th>Poste</th>
                            <th style={{ width: 140 }}>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMulti.map((opt) => (
                            <tr key={opt.value}>
                              <td className="fw-semibold">{opt.label}</td>
                              <td className="text-muted small">{opt.poste || "—"}</td>
                              <td>
                                {batchStatuses[opt.value] === "ok" ? (
                                  <Badge bg="success">Terminé</Badge>
                                ) : batchStatuses[opt.value] === "error" ? (
                                  <Badge bg="danger">Erreur</Badge>
                                ) : batchStatuses[opt.value] === "pending" ? (
                                  <Badge bg="warning" text="dark">En cours…</Badge>
                                ) : (
                                  <Badge bg="secondary">En attente</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="headingThree">
                <button className="accordion-button ${openAcc==='liste' ? '' : 'collapsed' }" type="button" onClick={()=>setOpenAcc(openAcc==='liste' ? '' : 'liste')} aria-expanded={openAcc==='liste'}>
                  📚 Liste des impressions deja effectuées
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${openAcc==='liste' ? 'show' : ''}`} aria-labelledby="headingThree">
                <div className="accordion-body">
                  <RHListFiches />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
    if (active === "attestation") {
      return (
        <div className="accordion mb-3">
          <div className="accordion-item">
            <h2 className="accordion-header" id="headingAtt1">
              <button className="accordion-button" type="button">🧾 Attestation de travail</button>
            </h2>
            <div className="accordion-collapse collapse show" aria-labelledby="headingAtt1">
              <div className="accordion-body">
                <div className="d-flex align-items-end gap-2 flex-wrap mb-3">
                  <div style={{ minWidth: 420 }}>
                    <Select
                      classNamePrefix="react-select"
                      options={employesOptions}
                      isSearchable
                      placeholder="Rechercher un employé..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      maxMenuHeight={480}
                      styles={{ menuPortal: (b)=>({ ...b, zIndex: 9999 }), control: (b)=>({ ...b, minWidth: 420 }) }}
                      value={selectedEmployeAtt ? employesOptions.find(o => String(o.value) === String(selectedEmployeAtt)) : null}
                      onChange={(opt) => setSelectedEmployeAtt(opt ? String(opt.value) : "")}
                    />
                  </div>
                  <Button
                    variant="success"
                    disabled={!selectedEmployeAtt || printAttestLoading}
                    onClick={async ()=>{
                      try {
                        setPrintAttestLoading(true);
                        const resp = await axios.post('/attestations/imprimer', null, { params: { id_employe: selectedEmployeAtt }, responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const link = document.createElement('a');
                        // Construire un nom déterministe: attestation_travail_nom_prenom_poste
                        let filename = 'attestation.pdf';
                        try {
                          const opt = employesOptions.find(o => String(o.value) === String(selectedEmployeAtt));
                          if (opt && opt.label) {
                            const [ident, postePart] = String(opt.label).split('|');
                            const [nom, prenom] = (ident || '').trim().split(' ');
                            const poste = (postePart || '').trim().replace(/\s+/g, '_');
                            const safeNom = (nom || '').replace(/\s+/g, '_');
                            const safePrenom = (prenom || '').replace(/\s+/g, '_');
                            filename = `attestation_travail_${safeNom}_${safePrenom}_${poste}.pdf`;
                          }
                        } catch {}
                        link.href = url;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
                      } catch(e) {} finally { setPrintAttestLoading(false); }
                    }}
                  >
                    {printAttestLoading ? (<><Spinner size="sm" animation="border" className="me-2"/>Impression...</>) : 'Imprimer attestation'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="accordion-item mt-3">
            <h2 className="accordion-header" id="headingAtt2">
              <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAtt2">📚 Liste des attestations générées</button>
            </h2>
            <div id="collapseAtt2" className="accordion-collapse collapse" aria-labelledby="headingAtt2">
              <div className="accordion-body">
                {/* Optionnel: je peux brancher GET /attestations/admin/logs ici si tu veux l'afficher */}
                <Alert variant="light" className="mb-0">Le journal des attestations (logs) est disponible côté API. Je peux lister ici sur demande.</Alert>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (active === "bulletins") {
      const filtered = items.filter(b => `${b.employe?.nom || ""} ${b.employe?.prenom || ""} ${b.mois || ""}`.toLowerCase().includes(search.toLowerCase()));
      return (
        <div className="table-responsive">
          <Table className="mb-0 table-soft">
            <thead className="table-light"><tr><th>Employé</th><th>Matricule</th><th>Département</th><th>Période</th><th>Date génération</th></tr></thead>
            <tbody>{filtered.map((b,i)=>(<tr key={i}><td>{b.employe?.nom} {b.employe?.prenom}</td><td>{b.employe?.matricule}</td><td>{b.employe?.departement || "—"}</td><td><Badge bg="secondary">{b.mois} {b.annee}</Badge></td><td>{b.date_generation}</td></tr>))}</tbody>
          </Table>
        </div>
      );
    }
    if (active === "paiements") {
      return (
        <>
          <Card className="mb-3 shadow-sm surface-card toolbar-card">
            <Card.Body>
              <div className="d-flex align-items-end gap-2 flex-wrap">
                <Form.Select value={rpAnnee} onChange={(e)=>setRpAnnee(e.target.value)} style={{ maxWidth: 160 }}>
                  <option value="">Année</option>
                  {annees.map(y => (<option key={y} value={y}>{y}</option>))}
                </Form.Select>
                <Form.Select value={rpMois} onChange={(e)=>setRpMois(e.target.value)} style={{ maxWidth: 200 }}>
                  <option value="">Mois</option>
                  {moisOptions.map(m => (<option key={m} value={m}>{m}</option>))}
                </Form.Select>
                <Button variant="primary" disabled={!rpAnnee || !rpMois || rpLoading} onClick={fetchRapportPaiement}>
                  {rpLoading ? (<><Spinner size="sm" animation="border" className="me-2"/>Chargement...</>) : 'Générer Rapport'}
                </Button>
                <Button variant="outline-secondary" disabled={!rpAnnee || !rpMois || !rpData} onClick={()=>exportRapportSection('sans_verrouille')}>Exporter Sans Verrouillé</Button>
                <Button variant="outline-secondary" disabled={!rpAnnee || !rpMois || !rpData} onClick={()=>exportRapportSection('verrouilles')}>Exporter Verrouillés</Button>
                <Button variant="success" disabled={!rpAnnee || !rpMois || !rpData || rpPrint} onClick={printRapport}>
                  {rpPrint ? (<><Spinner size="sm" animation="border" className="me-2"/>Impression...</>) : 'Imprimer'}
                </Button>
              </div>
            </Card.Body>
          </Card>

          {rpData && (
            <>
              <Row className="g-3 mb-3">
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Somme Net</div><div className="h5 fw-bold text-success">{formatCurrency(rpData.kpis.somme_net)}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Somme Brut</div><div className="h5 fw-bold text-primary">{formatCurrency(rpData.kpis.somme_brut)}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Charge Salariale</div><div className="h5 fw-bold text-warning">{formatCurrency(rpData.kpis.somme_charge_salariale)}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Charge Patronale</div><div className="h5 fw-bold text-danger">{formatCurrency(rpData.kpis.somme_charge_patronale)}</div></Card.Body></Card></Col>
              </Row>
              <Row className="g-3 mb-4">
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Contrats Actifs</div><div className="h5 fw-bold">{rpData.kpis.nb_contrats_actifs}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Bulletins déjà imprimés</div><div className="h5 fw-bold">{rpData.kpis.nb_salaires_verrouilles}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">Différence</div><div className="h5 fw-bold">{rpData.kpis.difference}</div></Card.Body></Card></Col>
                <Col md={3}><Card className="text-center h-100 shadow-sm"><Card.Body><div className="text-muted small">% Couverture</div><div className="h5 fw-bold">{rpData.kpis.couverture.toFixed(2)}%</div></Card.Body></Card></Col>
              </Row>

              <Tab.Container defaultActiveKey="sans">
                <Nav variant="pills" className="mb-3">
                  <Nav.Item><Nav.Link eventKey="sans">🔴 Bulletin Pas Totalement terminer</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="avec">🟢 Bulletins Terminer et Envoyer à Chaque Employé</Nav.Link></Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="sans">
                    <DataTable
                      columns={[
                        { key: 'nom', header: 'Nom' },
                        { key: 'prenom', header: 'Prénom' },
                        { key: 'poste', header: 'Poste' },
                        { key: 'periode', header: 'Période', render: (_v, r)=> (<Badge bg="secondary">{r.mois} {r.annee}</Badge>) },
                      ]}
                      rows={(rpData.sans_verrouille||[]).map(r=>({ ...r, periode: `${r.mois} ${r.annee}` }))}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="avec">
                    <DataTable
                      columns={[
                        { key: 'nom', header: 'Nom' },
                        { key: 'prenom', header: 'Prénom' },
                        { key: 'poste', header: 'Poste' },
                        { key: 'salaire_net', header: 'Net', render: v=> <span className="text-success">{formatCurrency(v)}</span> },
                        { key: 'charge_salariale', header: 'Charge S', render: v=> formatCurrency(v) },
                        { key: 'charge_patronale', header: 'Charge P', render: v=> formatCurrency(v) },
                      ]}
                      rows={(rpData.verrouilles||[])}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>

              {/* Graphique simple (journal) */}
              <Card className="mt-3">
                <Card.Header>📈 Évolution journalière des verrouillages</Card.Header>
                <Card.Body>
                  {rpJournal.length === 0 ? (
                    <div className="text-muted">Aucune donnée</div>
                  ) : (
                    <div className="d-flex align-items-end gap-2" style={{ height: 140 }}>
                      {rpJournal.map((d,i)=>(
                        <div key={i} style={{ width: 12, background: '#0d6efd', height: `${Math.min(100, d.count*10)}px` }} title={`${d.jour}: ${d.count}`} />
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </>
      );
    }
    // attestation: affichage liste employés pour sélectionner et imprimer via print navigateur (placeholder)
    return (
      <Alert variant="info">Sélectionnez un employé et utilisez Imprimer pour générer une attestation de travail.</Alert>
    );
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <Container fluid className="p-4 p-lg-5 rh-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Impressions" }]} />
          <header className="rh-page-header">
            <h4>
              <span className="rh-page-icon"><FaPrint /></span>
              Impressions de documents
            </h4>
            <p>Attestations, fiches de congés, listes employés et rapports de paiement mensuels.</p>
          </header>

          <Tab.Container activeKey={active} onSelect={(k)=>setActive(k)}>
            <Nav variant="pills" className="rh-tabs-pills">
                <Nav.Item><Nav.Link eventKey="attestation">Attestation de travail</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="conges">Fiche de congés</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="employes">Liste des employés</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="paiements">Rapport des paiements (mois)</Nav.Link></Nav.Item>
              </Nav>

            {Toolbar}

            <Tab.Content>
              <Tab.Pane eventKey="attestation"><SectionContent /></Tab.Pane>
              <Tab.Pane eventKey="conges"><SectionContent /></Tab.Pane>
              <Tab.Pane eventKey="employes"><SectionContent /></Tab.Pane>
              <Tab.Pane eventKey="paiements"><SectionContent /></Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </div>
    </div>
  );
};

export default ImpressionDocumentsPage;


